import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import { getFileIcon } from "../FileUpload";
import StatusMessage from "./StatusMessage";

// Throttle rapid value changes to at most once per `delay` ms
function useThrottledValue<T>(value: T, delay: number, enabled: boolean): T {
  const [throttled, setThrottled] = useState(value);
  const lastUpdate = useRef(0);
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) {
      setThrottled(value);
      return;
    }

    const now = Date.now();
    const elapsed = now - lastUpdate.current;

    if (elapsed >= delay) {
      lastUpdate.current = now;
      setThrottled(value);
    } else if (!pending.current) {
      pending.current = setTimeout(() => {
        lastUpdate.current = Date.now();
        setThrottled(value);
        pending.current = null;
      }, delay - elapsed);
    }

    return () => {
      if (pending.current) {
        clearTimeout(pending.current);
        pending.current = null;
      }
    };
  }, [value, delay, enabled]);

  // Always flush final value when streaming stops
  useEffect(() => {
    if (!enabled) setThrottled(value);
  }, [enabled, value]);

  return throttled;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  id?: string;
  attachment?: Array<File>;
  createdAt?: Date;
}

// Smart LaTeX rendering function
const processContentForRendering = (content: string, isStreaming: boolean = false): string => {
  // If streaming and content is long, only process the last portion for performance
  if (isStreaming && content.length > 200) {
    const lastPortion = content.slice(-200);
    const beforeLastPortion = content.slice(0, -200);
    
    // Find the last complete LaTeX expression in the before portion
    const lastCompleteLatex = beforeLastPortion.lastIndexOf('$');
    const beforeCompleteLatex = lastCompleteLatex > 0 ? beforeLastPortion.slice(0, lastCompleteLatex + 1) : beforeLastPortion;
    
    // Process LaTeX fixes only on the combined content we'll actually render
    const contentToProcess = beforeCompleteLatex + lastPortion;
    return processLatexDelimiters(contentToProcess);
  }
  
  // For complete messages or short content, process everything
  return processLatexDelimiters(content);
};

// Convert various LaTeX delimiter formats to $ delimiters for remark-math
const processLatexDelimiters = (content: string): string => {
  return content
    // Fix double-escaped delimiters first
    .replace(/\\\\\(/g, '\\(')
    .replace(/\\\\\)/g, '\\)')
    .replace(/\\\\\[/g, '\\[')
    .replace(/\\\\\]/g, '\\]')
    // Convert \( \) to $ $ for inline math
    .replace(/\\\(([^)]+)\\\)/g, '$$$1$$')
    // Convert \[ \] to $$ $$ for display math
    .replace(/\\\[([^\]]+)\\\]/g, '$$$$$$1$$$$');
};

// Check if content likely contains LaTeX that needs processing
const hasLatexContent = (content: string): boolean => {
  return content.includes('$') || content.includes('\\(') || content.includes('\\[');
};


// Memoized component for rendering individual messages
export const MessageItem = React.memo(({
  message,
  index,
  isStreaming = false
}: {
  message: ChatMessage,
  index: number,
  isStreaming?: boolean
}) => {
 if (!(message.role === "user" || (message.role === "assistant" && message.content.trim()))) {
    return null;
  }

  // Only the last assistant message can be the one currently streaming
  const isThisMessageStreaming = isStreaming && message.role === 'assistant';

  // Throttle content updates during streaming (~50ms between renders)
  const displayContent = useThrottledValue(message.content, 50, isThisMessageStreaming);

  // Use smart content processing (only for completed messages)
  const processedContent = React.useMemo(() => {
    if (isThisMessageStreaming) return displayContent;
    return processContentForRendering(displayContent, false);
  }, [displayContent, isThisMessageStreaming]);

  // Only use LaTeX plugins if content actually has LaTeX AND message is complete
  // KaTeX + ReactMarkdown parsing is expensive — skip entirely during streaming
  const shouldUseLaTeX = React.useMemo(() => {
    return !isThisMessageStreaming && hasLatexContent(displayContent);
  }, [displayContent, isThisMessageStreaming]);

  return (
    <div
      key={message.id ?? index}
      className={`flex ${message.role === "user" ? "justify-end" : "justify-start items-start"} min-w-0`}
    >
      <div
        className={`break-words rounded-lg px-3 py-2 text-sm overflow-hidden min-w-0 ${
          message.role === "user"
            ? "max-w-[70%] text-white user-message"
            : "flex-1 bg-transparent text-gray-900 assistant-message"
        }`}
        style={message.role === "user" ? { backgroundColor: "#1e9cd7" } : {}}
      >
        {message.attachment && message.attachment.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-1">
            {message.attachment.map((file, idx) => (
              <span key={idx} className="flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs mr-1">
                {getFileIcon(file)} {file.name}
              </span>
            ))}
          </div>
        )}
        
        {message.content.includes('[File Information]') ? (
          (() => {
            const lines = message.content.split('\n');
            const fileInfo = lines.find((line: string) => line.includes('Original name:'));
            const fileName = fileInfo
              ? fileInfo.split('Original name:')[1].trim()
              : null;

            return (
              <div className="flex flex-col gap-1">
                <div className="text-sm">{message.content}</div>
                {fileName && <div className="text-xs text-gray-500">📎 {fileName}</div>}
              </div>
            );
          })()
        ) : isThisMessageStreaming ? (
          // During streaming: render plain text for maximum responsiveness
          // Full markdown + KaTeX rendering happens once the message is complete
          <div className="markdown-content min-w-0 max-w-full whitespace-pre-wrap" style={{ maxWidth: '100%', overflowX: 'hidden' }}>
            {displayContent}
          </div>
        ) : (
          <div className="markdown-content min-w-0 max-w-full" style={{ maxWidth: '100%', overflowX: 'hidden', contain: 'layout style' }}>
            <ReactMarkdown
              remarkPlugins={shouldUseLaTeX ? [remarkGfm, remarkMath] : [remarkGfm]}
              rehypePlugins={shouldUseLaTeX ? [rehypeKatex] : []}
              components={{
                // Custom component to handle math blocks
                div: ({ className, children, ...props }) => {
                  if (className?.includes('math')) {
                    return (
                      <div
                        className={className}
                        style={{
                          maxWidth: '100%',
                          contain: 'layout style'
                        }}
                        {...props}
                      >
                        {children}
                      </div>
                    );
                  }
                  return <div className={className} {...props}>{children}</div>;
                }
              }}
            >
              {processedContent}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memoization
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.role === nextProps.message.role &&
    prevProps.index === nextProps.index &&
    prevProps.isStreaming === nextProps.isStreaming
  );
});

MessageItem.displayName = 'MessageItem';


// Helper component for messages container
export default function MessagesContainer({
  messages,
  messagesHeightClass,
  bottomRef,
  isStreaming = false,
  agentStatus
}: {
  messages: ChatMessage[],
  messagesHeightClass?: string,
  bottomRef: React.RefObject<HTMLDivElement | null>,
  isStreaming?: boolean,
  agentStatus?: string | null
}) {
  return (
    <div className={`${messagesHeightClass || "h-36"} overflow-y-auto overflow-x-hidden px-4 py-2 space-y-2 min-w-0 max-w-full`}>
      {messages.map((m, i) => (
        <MessageItem
          key={m.id ?? i}
          message={m}
          index={i}
          isStreaming={isStreaming && i === messages.length - 1}
        />
      ))}
      {agentStatus !== null && (
        <StatusMessage status={agentStatus} />
      )}
      <div ref={bottomRef} />
    </div>
  );
}